import * as Sentry from "@sentry/react";

const SENSITIVE_KEY_PATTERN =
  /password|token|secret|authorization|apikey|api_key|access_token|refresh_token|confirmation|phone|email/i;

function scrubSensitiveValues(value, depth = 0) {
  if (depth > 4 || value == null) return value;

  if (Array.isArray(value)) {
    return value.map((item) => scrubSensitiveValues(item, depth + 1));
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((next, [key, item]) => {
      next[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? "[Filtered]"
        : scrubSensitiveValues(item, depth + 1);
      return next;
    }, {});
  }

  if (typeof value === "string") {
    return value
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[Filtered Email]")
      .replace(/\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, "[Filtered Phone]");
  }

  return value;
}

function scrubUrl(url) {
  if (!url || typeof url !== "string") return url;

  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split("?")[0].split("#")[0];
  }
}

function scrubException(exception) {
  if (!exception?.values) return exception;

  return {
    ...exception,
    values: exception.values.map((value) => ({
      ...value,
      value: scrubSensitiveValues(value.value),
    })),
  };
}

function scrubBreadcrumbs(breadcrumbs) {
  if (!Array.isArray(breadcrumbs)) return breadcrumbs;

  return breadcrumbs.map((breadcrumb) => ({
    ...breadcrumb,
    message: scrubSensitiveValues(breadcrumb.message),
    data: scrubSensitiveValues(breadcrumb.data),
  }));
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV === "production" ? "portal-beta" : "development",
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "NetworkError when attempting to fetch resource",
      "Load failed",
      "Failed to fetch",
    ],
    beforeSend(event) {
      const nextEvent = { ...event };
      delete nextEvent.user;

      if (nextEvent.request) {
        nextEvent.request = {
          url: scrubUrl(nextEvent.request.url),
          method: nextEvent.request.method,
        };
      }

      nextEvent.exception = scrubException(nextEvent.exception);
      nextEvent.breadcrumbs = scrubBreadcrumbs(nextEvent.breadcrumbs);
      nextEvent.extra = scrubSensitiveValues(nextEvent.extra);
      nextEvent.contexts = scrubSensitiveValues(nextEvent.contexts);
      nextEvent.tags = {
        ...nextEvent.tags,
        app: "strobe-portal",
      };

      return nextEvent;
    },
  });
}

export function capturePortalError(error, context = {}) {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    Object.entries(scrubSensitiveValues(context) || {}).forEach(([key, value]) => {
      scope.setContext(key, value && typeof value === "object" ? value : { value });
    });
    Sentry.captureException(error);
  });
}

export { Sentry };
